import { useState } from "react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";
import type {
  ClassificationResult,
  ExtractedAnswer,
  ConditionUserOverride,
} from "../types";

interface UseDueDiligenceAnswersReturn {
  expandedDdAnswers: Set<string>;
  expandedConditionDetails: Set<string>;
  expandedExistingAnswers: Set<string>;
  expandedAIDetails: Set<string>;
  expandedStandardFields: Set<string>;
  editedDdAnswers: Record<string, Record<number, ExtractedAnswer>>;
  skippedDdAnswers: Record<string, Set<number>>;
  conditionOverrides: Record<string, ConditionUserOverride>;
  linkingLabelToQuestion: { questionId: number; labelId: number; labelName: string } | null;
  toggleDdAnswersExpanded: (fileId: string) => void;
  toggleConditionDetailsExpanded: (key: string) => void;
  toggleExistingAnswerExpanded: (key: string) => void;
  toggleAIDetailsExpanded: (key: string) => void;
  toggleStandardFieldsExpanded: (fileId: string) => void;
  updateEditedDdAnswer: (fileId: string, questionId: number, field: keyof ExtractedAnswer, value: any) => void;
  toggleSkippedDdAnswer: (fileId: string, questionId: number) => void;
  isDdAnswerSkipped: (fileId: string, questionId: number) => boolean;
  getEffectiveDdAnswer: (fileId: string, questionId: number) => ExtractedAnswer | undefined;
  toggleConditionOverride: (fileId: string, questionId: number, conditionId: string, originalPassed: boolean) => void;
  updateConditionOverrideReason: (fileId: string, questionId: number, conditionId: string, reason: string) => void;
  getEffectiveConditionPassed: (fileId: string, questionId: number, conditionId: string, originalPassed: boolean) => boolean;
  getConditionOverride: (fileId: string, questionId: number, conditionId: string) => ConditionUserOverride | undefined;
  getConditionOverridesForQuestion: (fileId: string, questionId: number) => Record<string, ConditionUserOverride>;
  prepareDdAnswersForSave: (fileId: string) => any;
  handleLinkLabelToQuestion: (questionId: number, labelId: number, labelName: string, questionText: string) => Promise<void>;
  clearDdStateForFile: (fileId: string) => void;
}

export function useDueDiligenceAnswers(
  session: any,
  classificationResults: Record<string, ClassificationResult>
): UseDueDiligenceAnswersReturn {
  const [expandedDdAnswers, setExpandedDdAnswers] = useState<Set<string>>(new Set());
  const [expandedConditionDetails, setExpandedConditionDetails] = useState<Set<string>>(new Set());
  const [expandedExistingAnswers, setExpandedExistingAnswers] = useState<Set<string>>(new Set());
  const [expandedAIDetails, setExpandedAIDetails] = useState<Set<string>>(new Set());
  const [expandedStandardFields, setExpandedStandardFields] = useState<Set<string>>(new Set());
  const [editedDdAnswers, setEditedDdAnswers] = useState<Record<string, Record<number, ExtractedAnswer>>>({});
  const [skippedDdAnswers, setSkippedDdAnswers] = useState<Record<string, Set<number>>>({});
  const [conditionOverrides, setConditionOverrides] = useState<Record<string, ConditionUserOverride>>({});
  const [linkingLabelToQuestion, setLinkingLabelToQuestion] = useState<{
    questionId: number;
    labelId: number;
    labelName: string;
  } | null>(null);

  const toggleSet = (setter: React.Dispatch<React.SetStateAction<Set<string>>>, key: string) => {
    setter((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const toggleDdAnswersExpanded = (fileId: string) => toggleSet(setExpandedDdAnswers, fileId);
  const toggleConditionDetailsExpanded = (key: string) => toggleSet(setExpandedConditionDetails, key);
  const toggleExistingAnswerExpanded = (key: string) => toggleSet(setExpandedExistingAnswers, key);
  const toggleAIDetailsExpanded = (key: string) => toggleSet(setExpandedAIDetails, key);
  const toggleStandardFieldsExpanded = (fileId: string) => toggleSet(setExpandedStandardFields, fileId);

  const updateEditedDdAnswer = (
    fileId: string,
    questionId: number,
    field: keyof ExtractedAnswer,
    value: any
  ) => {
    setEditedDdAnswers((prev) => {
      const fileAnswers = prev[fileId] || {};
      const classification = classificationResults[fileId];
      const originalAnswer = classification?.dueDiligenceAnswers?.extractedAnswers.find(
        (a) => a.questionId === questionId
      );

      const currentAnswer = fileAnswers[questionId] || originalAnswer;
      if (!currentAnswer) return prev;

      return {
        ...prev,
        [fileId]: {
          ...fileAnswers,
          [questionId]: {
            ...currentAnswer,
            [field]: value,
          },
        },
      };
    });
  };

  const toggleSkippedDdAnswer = (fileId: string, questionId: number) => {
    setSkippedDdAnswers((prev) => {
      const fileSkipped = prev[fileId] || new Set<number>();
      const newSkipped = new Set(fileSkipped);

      if (newSkipped.has(questionId)) {
        newSkipped.delete(questionId);
      } else {
        newSkipped.add(questionId);
      }

      return {
        ...prev,
        [fileId]: newSkipped,
      };
    });
  };

  const isDdAnswerSkipped = (fileId: string, questionId: number): boolean => {
    return skippedDdAnswers[fileId]?.has(questionId) ?? false;
  };

  const getEffectiveDdAnswer = (fileId: string, questionId: number): ExtractedAnswer | undefined => {
    const editedAnswer = editedDdAnswers[fileId]?.[questionId];
    if (editedAnswer) return editedAnswer;

    const classification = classificationResults[fileId];
    return classification?.dueDiligenceAnswers?.extractedAnswers.find(
      (a) => a.questionId === questionId
    );
  };

  const toggleConditionOverride = (
    fileId: string,
    questionId: number,
    conditionId: string,
    originalPassed: boolean
  ) => {
    const key = `${fileId}-${questionId}-${conditionId}`;
    setConditionOverrides((prev) => {
      const existing = prev[key];
      if (existing?.overridden) {
        const { [key]: removed, ...rest } = prev;
        return rest;
      } else {
        return {
          ...prev,
          [key]: {
            overridden: true,
            originalPassed,
            userValue: !originalPassed,
            reason: '',
            overriddenAt: new Date().toISOString(),
          },
        };
      }
    });
  };

  const updateConditionOverrideReason = (
    fileId: string,
    questionId: number,
    conditionId: string,
    reason: string
  ) => {
    const key = `${fileId}-${questionId}-${conditionId}`;
    setConditionOverrides((prev) => {
      const existing = prev[key];
      if (!existing) return prev;
      return {
        ...prev,
        [key]: {
          ...existing,
          reason,
        },
      };
    });
  };

  const getEffectiveConditionPassed = (
    fileId: string,
    questionId: number,
    conditionId: string,
    originalPassed: boolean
  ): boolean => {
    const key = `${fileId}-${questionId}-${conditionId}`;
    const override = conditionOverrides[key];
    if (override?.overridden) {
      return override.userValue;
    }
    return originalPassed;
  };

  const getConditionOverride = (
    fileId: string,
    questionId: number,
    conditionId: string
  ): ConditionUserOverride | undefined => {
    const key = `${fileId}-${questionId}-${conditionId}`;
    return conditionOverrides[key];
  };

  const getConditionOverridesForQuestion = (fileId: string, questionId: number): Record<string, ConditionUserOverride> => {
    const prefix = `${fileId}-${questionId}-`;
    const overrides: Record<string, ConditionUserOverride> = {};

    Object.entries(conditionOverrides).forEach(([key, value]) => {
      if (key.startsWith(prefix)) {
        const conditionId = key.substring(prefix.length);
        overrides[conditionId] = value;
      }
    });

    return overrides;
  };

  const prepareDdAnswersForSave = (fileId: string) => {
    const classification = classificationResults[fileId];
    if (!classification?.dueDiligenceAnswers?.extractedAnswers.length) {
      return undefined;
    }

    return classification.dueDiligenceAnswers.extractedAnswers.map((answer) => {
      if (isDdAnswerSkipped(fileId, answer.questionId)) {
        return null;
      }

      const effective = getEffectiveDdAnswer(fileId, answer.questionId);
      if (!effective) return null;

      const questionOverrides = getConditionOverridesForQuestion(fileId, answer.questionId);
      const hasOverrides = Object.keys(questionOverrides).length > 0;

      return {
        questionId: effective.questionId,
        answerType: effective.answerType,
        extractedValue: effective.answerType === 'options'
          ? null
          : effective.extractedValue,
        matchedOptionId: effective.matchedOptionId,
        aiConfidence: effective.aiConfidence,
        aiRawResponse: effective.aiRawResponse,
        ...(hasOverrides && { conditionOverrides: questionOverrides }),
      };
    }).filter(Boolean);
  };

  const handleLinkLabelToQuestion = async (
    questionId: number,
    labelId: number,
    labelName: string,
    questionText: string
  ) => {
    if (!session) return;

    setLinkingLabelToQuestion({ questionId, labelId, labelName });

    try {
      const response = await fetchWithAuth(
        `${API_URL}/due-diligence-categories/questions/${questionId}/add-label`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ labelId }),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Fout bij koppelen label (${response.status})`
        );
      }

      await response.json();
      toast.success(
        `Label "${labelName}" gekoppeld aan vraag: "${questionText.substring(0, 50)}${questionText.length > 50 ? '...' : ''}"`
      );
    } catch (err: any) {
      console.error("Error linking label to question:", err);
      Sentry.captureException(err, {
        extra: { questionId, labelId, labelName },
      });
      toast.error(err.message || "Fout bij koppelen van label aan vraag");
    } finally {
      setLinkingLabelToQuestion(null);
    }
  };

  const clearDdStateForFile = (fileId: string) => {
    setEditedDdAnswers((prev) => {
      const newEdited = { ...prev };
      delete newEdited[fileId];
      return newEdited;
    });
    setExpandedDdAnswers((prev) => {
      const newSet = new Set(prev);
      newSet.delete(fileId);
      return newSet;
    });
  };

  return {
    expandedDdAnswers,
    expandedConditionDetails,
    expandedExistingAnswers,
    expandedAIDetails,
    expandedStandardFields,
    editedDdAnswers,
    skippedDdAnswers,
    conditionOverrides,
    linkingLabelToQuestion,
    toggleDdAnswersExpanded,
    toggleConditionDetailsExpanded,
    toggleExistingAnswerExpanded,
    toggleAIDetailsExpanded,
    toggleStandardFieldsExpanded,
    updateEditedDdAnswer,
    toggleSkippedDdAnswer,
    isDdAnswerSkipped,
    getEffectiveDdAnswer,
    toggleConditionOverride,
    updateConditionOverrideReason,
    getEffectiveConditionPassed,
    getConditionOverride,
    getConditionOverridesForQuestion,
    prepareDdAnswersForSave,
    handleLinkLabelToQuestion,
    clearDdStateForFile,
  };
}
